import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { App, Layout, Drawer, Menu, Input, Space, } from 'antd';
import { DoubleRightOutlined, DoubleLeftOutlined, DownloadOutlined, FormOutlined, SettingOutlined, CommentOutlined, PlayCircleOutlined, FileWordOutlined, FilePdfOutlined, AudioOutlined, TagsOutlined, MoreOutlined, FireOutlined, FormatPainterOutlined, UnorderedListOutlined, TeamOutlined, UsergroupDeleteOutlined, UsergroupAddOutlined, } from '@ant-design/icons';
import ResponsiveTooltip from 'components/ResponsiveTooltip';
import { useAppContext, screenBreakpoints } from 'App/app-context';
import { useBeeyTutorial } from 'screens/DashboardScreen/beeyTutorial';
import * as clientEnv from 'libs/client-env';
import { txt } from 'libs/i18n';
import { TranslateIcon, WandSvg } from 'components/CustomIcons';
import { buildMediaFileUrl, fetchExport } from 'api/project-api';
import { fetchRefinerJob, polishTrsx } from 'api/model/services/services-api';
import dayjs from 'dayjs';
import { useSession } from 'components/Authenticated';
import { fetchExportTemplates } from 'api/team-api';
import { SubtitlesFormatSchema } from 'api/settings/user-settings';
import { useComputed, useSignals } from '@preact/signals-react/runtime';
import SecondaryActionButton from 'components/SecondaryActionButton';
import ControlsButton from './ControlsButton';
import { disabledGuards } from './disabledGuards';
import ProjectMenuAdvanced from './ProjectMenuAdvanced';
import { useSubModeTutorial } from '../contexts/subModeTutorial';
import { downloadBlobAsFile, downloadUrl } from './exports';
import { watchRefinerJob } from './refiner-watch';
import MemoizedProjectMenuModals from './ProjectMenuModals';
import { useEditorContext } from '../contexts/projectSignal';
import styles from './style.module.less';
import TagsSection from './TagsSection';
import { findTranslateAssignment } from '../signalsStore/aiAssignments';
import { setActiveModalSignal } from '../signalsStore/activeModal';
const { Sider } = Layout;
const { TextArea } = Input;
// Refiner pipeline phase -> localization key for the progress toast.
const REFINER_PHASE_KEYS = {
    llm: 'polishTrsxPhaseLlm',
    fixer: 'polishTrsxPhaseFixer',
    deepagent: 'polishTrsxPhaseLlm',
    scenes: 'polishTrsxPhaseScenes',
    speakers: 'polishTrsxPhaseSpeakers',
    verifier: 'polishTrsxPhaseVerifier',
};
const MemoizedProjectMenuPanel = ({ mediaProcessingStatus, mediaSource, isCaptionMode, editorController, isWaveVisible, onWaveVisibilityChange, enqueueProjectUpdate, isLocked, projectFiles, }) => {
    var _a, _b;
    const { projectSignal } = useEditorContext();
    const { screenSize } = useAppContext();
    const { session, updateUserSettings } = useSession();
    const { message, modal } = App.useApp();
    const beeyTutorialContext = useBeeyTutorial();
    const subModeTutorialContext = useSubModeTutorial();
    const isMobile = screenSize.width < screenBreakpoints.md;
    const { settings } = session.login.user;
    // The refiner runs as a Beey service behind the services proxy; the proxy
    // authorizes per-user via the registered services:refiner claim (fixed name,
    // not config-dependent), so the button mirrors that gate.
    const hasRefinerService = session.login.hasClaim('services:refiner');
    useSignals();
    const projectStart = useComputed(() => projectSignal.value.description.start).value;
    const projectAuthor = useComputed(() => projectSignal.value.description.author).value;
    const notes = useComputed(() => projectSignal.value.description.notes).value;
    const hasVideoSignal = useComputed(() => { var _a; return (_a = projectSignal.value.mediaInfo) === null || _a === void 0 ? void 0 : _a.hasVideo; }).value;
    const hasRecording = useComputed(() => projectSignal.value.hasRecording).value;
    const isReadOnly = useComputed(() => projectSignal.value.isReadOnly).value;
    const isTeamProject = useComputed(() => projectSignal.value.isTeamProject).value;
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [exportTemplates, setExportTemplates] = useState([]);
    const [userCollapsed, setUserCollapsed] = useState(isMobile || clientEnv.getIsMenuCollapsed());
    const [submenuItemsOpen, setSubmenuItemsOpen] = useState(clientEnv.getProjectSidemenuItemsOpen());
    const [isRefining, setIsRefining] = useState(false);
    const collapsed = userCollapsed || subModeTutorialContext.retrieveTutorialState() === 'running' || beeyTutorialContext.retrieveTutorialState() === 'running';
    const isRightToLeft = (_b = (_a = editorController.getLanguage()) === null || _a === void 0 ? void 0 : _a.isRightToLeft()) !== null && _b !== void 0 ? _b : false;
    const hasSubsClaim = session.login.hasClaim('subtitles:all')
        || SubtitlesFormatSchema.values.some((format) => session.login.hasClaim(`subtitles:${format}`));
    const disabledChecks = {
        readOnly: isReadOnly ? txt('readOnlyProject') : null,
        mediaProcessingComplete: mediaProcessingStatus === 'completed' ? null : txt('preCompleted'),
        mediaReady: hasRecording === false
            ? txt('projectHasNoRecording')
            : (mediaSource.isPackaged() === true ? null : txt('preMediaReady')),
        subtitlesExportAllowed: hasSubsClaim ? null : txt('subtitlesExportForbidden'),
        editorHasContent: (mediaProcessingStatus === 'completed' || mediaProcessingStatus === 'words-arriving'
            ? null
            : txt('noTranscription')),
        isLocked: isLocked ? txt('readOnlyProject') : null,
        isCaptionMode: isCaptionMode ? txt('captionModeWarning') : null,
        isRefining: isRefining ? `${txt('polishTrsxRunning')}…` : null,
    };
    const prepareSubModeTutorial = () => {
        updateUserSettings({ startSubModeTutorial: true });
    };
    const handleDownloadBlob = async (format) => {
        await enqueueProjectUpdate(async (initialProject) => {
            const blob = await fetchExport(session.connection, projectSignal.value, format, settings.enableTimestampLabels, isRightToLeft);
            downloadBlobAsFile(`${projectSignal.value.description.name}.${format}`, blob);
            return initialProject;
        });
    };
    const refinerStopRef = useRef(null);
    const stopRefinerWatch = () => {
        var _a;
        (_a = refinerStopRef.current) === null || _a === void 0 ? void 0 : _a.call(refinerStopRef);
        refinerStopRef.current = null;
    };
    useEffect(() => () => {
        // Leaving the editor: stop watching and drop the sticky loading toast —
        // message lives in the app-level antd context and would outlive this panel.
        stopRefinerWatch();
        message.destroy('polishTrsx');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const showRefinerProgress = (job) => {
        var _a;
        // Surface the pipeline phase (and percent, during the chunked pass) in the
        // loading toast. Same key -> updates in place.
        const phaseKey = job.progress == null
            ? undefined
            : REFINER_PHASE_KEYS[job.progress.phase];
        const percent = (_a = job.progress) === null || _a === void 0 ? void 0 : _a.percent;
        const detail = phaseKey === undefined
            ? ''
            : `: ${txt(phaseKey)}${percent == null ? '' : ` (${percent} %)`}`;
        void message.loading({
            content: `${txt('polishTrsxRunning')}${detail}…`,
            key: 'polishTrsx',
            duration: 0,
        });
    };
    const handlePolishTrsx = async () => {
        const result = await polishTrsx(session.connection, projectSignal.value.id);
        if (result.isFail()) {
            global.logger.error('failed to start trsx refinement', { error: result.err() });
            void message.error(txt('polishTrsxFailed'));
            return;
        }
        const jobId = result.get().id;
        stopRefinerWatch();
        setIsRefining(true);
        void message.loading({ content: `${txt('polishTrsxRunning')}…`, key: 'polishTrsx', duration: 0 });
        // The refiner pushes the refined trsx back into this project on its own
        // (auto_roundtrip); we only watch the job and report the outcome.
        refinerStopRef.current = watchRefinerJob(() => fetchRefinerJob(session.connection, jobId), {
            onProgress: showRefinerProgress,
            onSuccess: () => {
                setIsRefining(false);
                message.destroy('polishTrsx');
                void modal.confirm({
                    title: txt('polishTrsxDone'),
                    content: txt('polishTrsxReload'),
                    onOk: () => window.location.reload(),
                });
            },
            onFailure: (job) => {
                var _a;
                setIsRefining(false);
                global.logger.error('trsx refinement failed', {
                    jobId, error: (_a = job === null || job === void 0 ? void 0 : job.error) !== null && _a !== void 0 ? _a : job === null || job === void 0 ? void 0 : job.beey_push_error,
                });
                void message.error({ content: txt('polishTrsxFailed'), key: 'polishTrsx', duration: 6 });
            },
            onGiveUp: () => {
                setIsRefining(false);
                void message.warning({ content: txt('polishTrsxTimeout'), key: 'polishTrsx', duration: 8 });
            },
        });
    };
    const handleDownloadMedia = () => {
        const url = buildMediaFileUrl(projectSignal.value);
        downloadUrl(url, `${projectSignal.value.description.name}.mp4`);
    };
    const fetchExportTemplatesState = async () => {
        const templates = await fetchExportTemplates(session.connection);
        setExportTemplates(templates);
    };
    useEffect(() => {
        beeyTutorialContext.tutorialContinue();
    }, [beeyTutorialContext.currentStep]);
    useEffect(() => {
        void fetchExportTemplatesState();
    }, []);
    const handleMenuCollapse = (collapse) => {
        setUserCollapsed(collapse);
        clientEnv.setIsMenuCollapsed(collapse);
    };
    const items = [
        {
            key: 'editSpeakers',
            label: txt('editSpeakers'),
            icon: _jsx(TeamOutlined, {}),
            children: [{
                    label: (_jsx(ControlsButton, { icon: _jsx(UsergroupDeleteOutlined, {}), label: txt('deleteAllSpeakers'), action: () => setActiveModalSignal({ type: 'deleteSpeakers' }), disabledMessage: disabledGuards([
                            disabledChecks.mediaProcessingComplete,
                            disabledChecks.isLocked,
                            disabledChecks.readOnly,
                            disabledChecks.isCaptionMode,
                        ]), className: collapsed ? styles.buttonDropdown : '' })),
                    key: 'deleteAllSpeakers',
                    className: styles.submenu,
                },
                {
                    label: (_jsx(ControlsButton, { icon: _jsx(UsergroupAddOutlined, {}), label: txt('changeSpeakersNumber'), action: () => setActiveModalSignal({ type: 'changeSpeakersNumber' }), disabledMessage: disabledGuards([
                            disabledChecks.mediaProcessingComplete,
                            disabledChecks.isLocked,
                            disabledChecks.readOnly,
                            disabledChecks.isCaptionMode,
                        ]), className: collapsed ? styles.buttonDropdown : '' })),
                    key: 'changeSpeakersAmount',
                    className: styles.submenu,
                },
            ],
        },
        session.login.hasClaim('editor:subtitleMode') && hasRecording === true ? {
            key: 'subtitleMode',
            icon: _jsx(CommentOutlined, { className: "sub-mode-tutorial-step-settings" }),
            label: txt('subtitleMode'),
            children: [{
                    key: 'subtitles',
                    label: (_jsx(ControlsButton, { icon: isCaptionMode ? _jsx(SettingOutlined, {}) : _jsx(CommentOutlined, {}), label: isCaptionMode ? txt('subtitleSettings') : txt('convertToSubtitles'), action: () => {
                            setActiveModalSignal({ type: 'captionReview' });
                            subModeTutorialContext.tutorialPrepareNext();
                        }, disabledMessage: disabledGuards([
                            disabledChecks.mediaProcessingComplete,
                            disabledChecks.readOnly,
                            disabledChecks.isLocked,
                        ]), className: `sub-mode-tutorial-step-choose-settings ${collapsed ? styles.buttonDropdown : ''}` })),
                    className: styles.submenu,
                },
                (isCaptionMode && screenSize.width > screenBreakpoints.lg) && {
                    label: (_jsx(ControlsButton, { icon: _jsx(PlayCircleOutlined, {}), label: txt('runTutorial'), action: prepareSubModeTutorial, className: collapsed ? styles.buttonDropdown : '', disabledMessage: disabledGuards([
                            disabledChecks.mediaProcessingComplete,
                            disabledChecks.readOnly,
                            disabledChecks.isLocked,
                        ]) })),
                    key: 'tutorial',
                    className: styles.submenu,
                },
            ],
        } : null,
        session.login.hasClaim('aitools:all') || hasRefinerService
            ? {
                key: 'aiTools',
                label: txt('aiTools'),
                icon: _jsx("span", { children: _jsx(WandSvg, {}) }),
                children: [
                    session.login.hasClaim('aitools:all')
                        ? {
                            label: (_jsx(ControlsButton, { icon: _jsx(UnorderedListOutlined, {}), label: txt('smartEdits'), action: () => setActiveModalSignal({ type: 'ai', value: null }), disabledMessage: disabledGuards([
                                    disabledChecks.mediaProcessingComplete,
                                    disabledChecks.readOnly,
                                    disabledChecks.isLocked,
                                ]), className: clsx({ [styles.buttonDropdown]: collapsed }) })),
                            key: 'summarization',
                            className: styles.submenu,
                        } : null,
                    session.login.hasClaim('aitools:all') && findTranslateAssignment() !== undefined
                        ? {
                            label: (_jsx(ControlsButton, { icon: _jsx(TranslateIcon, {}), label: txt('smartTranslation'), action: () => setActiveModalSignal({ type: 'ai', value: 'Translation' }), disabledMessage: disabledGuards([
                                    disabledChecks.mediaProcessingComplete,
                                    disabledChecks.readOnly,
                                    disabledChecks.isLocked,
                                ]), className: collapsed ? styles.buttonDropdown : '' })),
                            key: 'smartTranslation',
                            className: styles.submenu,
                        } : null,
                    hasRefinerService
                        ? {
                            label: (_jsx(ControlsButton, { icon: _jsx(FormatPainterOutlined, {}), label: txt('polishTrsx'), busyMessage: `${txt('polishTrsxStarting')}…`, asyncAction: handlePolishTrsx, disabledMessage: disabledGuards([
                                    disabledChecks.isRefining,
                                    disabledChecks.mediaProcessingComplete,
                                    disabledChecks.readOnly,
                                    disabledChecks.isLocked,
                                ]), className: collapsed ? styles.buttonDropdown : '' })),
                            key: 'polishTrsx',
                            className: styles.submenu,
                        } : null,
                ],
            } : null,
        {
            key: 'download',
            label: txt('download'),
            icon: _jsx(DownloadOutlined, { className: "beey-tutorial-step-subtitles" }),
            children: [{
                    label: (_jsx(ControlsButton, { icon: _jsx(FileWordOutlined, {}), label: txt('downloadDoc'), busyMessage: `${txt('downloading')}…`, action: exportTemplates.length > 0
                            ? () => setActiveModalSignal({ type: 'download', value: 'docx' })
                            : undefined, asyncAction: exportTemplates.length > 0
                            ? undefined
                            : () => handleDownloadBlob('docx'), disabledMessage: disabledGuards([
                            disabledChecks.mediaProcessingComplete,
                        ]), className: collapsed ? styles.buttonDropdown : '' })),
                    key: 'downloadDoc',
                    className: styles.submenu,
                },
                {
                    label: (_jsx(ControlsButton, { icon: _jsx(FilePdfOutlined, {}), label: txt('downloadPdf'), busyMessage: `${txt('downloading')}…`, action: exportTemplates.length > 0
                            ? () => setActiveModalSignal({ type: 'download', value: 'pdf' })
                            : undefined, asyncAction: exportTemplates.length > 0
                            ? undefined
                            : () => handleDownloadBlob('pdf'), disabledMessage: disabledGuards([
                            disabledChecks.mediaProcessingComplete,
                        ]), className: collapsed ? styles.buttonDropdown : '' })),
                    key: 'downloadPdf',
                    className: styles.submenu,
                },
                hasRecording === true && session.login.hasClaim('file:download') && {
                    label: (_jsx(ControlsButton, { icon: _jsx(AudioOutlined, {}), label: txt('dlMedia'), action: session.login.hasClaim('services:transcode')
                            ? () => setActiveModalSignal({ type: 'cutMedia' })
                            : handleDownloadMedia, disabledMessage: disabledGuards([
                            disabledChecks.mediaReady,
                        ]), className: collapsed ? styles.buttonDropdown : '' })),
                    key: 'downloadMedia',
                    className: styles.submenu,
                },
                (hasVideoSignal !== null && hasVideoSignal !== void 0 ? hasVideoSignal : false) && session.login.hasClaim('file:download') && {
                    label: (_jsx(ControlsButton, { icon: _jsx(FireOutlined, {}), label: txt('burnSubtitlesInVideo'), accessClaim: "services:transcode", action: () => {
                            setActiveModalSignal({ type: 'burnSubtitles' });
                        }, disabledMessage: disabledGuards([
                            disabledChecks.mediaProcessingComplete,
                            disabledChecks.subtitlesExportAllowed,
                        ]), className: collapsed ? styles.buttonDropdown : '' })),
                    key: 'burnSubtitles',
                    className: styles.submenu,
                },
                hasRecording === true && {
                    label: (_jsx(ControlsButton, { className: `${collapsed ? styles.buttonDropdown : ''} beey-tutorial-step-choose-subs`, icon: _jsx(DownloadOutlined, {}), label: txt('downloadSubtitles'), action: () => {
                            setActiveModalSignal({ type: 'downloadSubtitles' });
                            beeyTutorialContext.tutorialPrepareNext();
                        }, disabledMessage: disabledGuards([
                            disabledChecks.mediaProcessingComplete,
                            disabledChecks.subtitlesExportAllowed,
                        ]) })),
                    key: 'downloadSubtitles',
                    className: styles.submenu,
                },
            ],
        },
        {
            key: 'metadata',
            label: txt('aboutProject'),
            icon: _jsx(FormOutlined, {}),
            children: [
                {
                    label: (_jsxs("div", { className: styles.aboutSection, children: [collapsed ? (_jsxs(Space, { className: styles.notesTitle, children: [_jsx(FormOutlined, {}), _jsx("span", { children: txt('aboutProject') })] })) : null, _jsxs("div", { children: [isTeamProject && (_jsx("strong", { className: styles.description, children: txt('teamProject') })), _jsxs("div", { className: styles.description, children: [_jsxs("div", { children: [txt('author'), ":"] }), _jsx("div", { className: styles.authorName, children: projectAuthor })] }), _jsxs("div", { className: styles.description, children: [_jsxs("div", { children: [txt('transcriptionStart'), ":"] }), _jsx("div", { className: styles.authorName, children: projectStart === null ? '' : dayjs(projectStart).format('DD/MM/YYYY HH:mm:ss') })] }), _jsxs("div", { className: styles.notes, children: [txt('notes'), ":"] }), _jsx("div", { className: styles.textArea, children: _jsx(TextArea, { disabled: true, className: styles.notesContent, rows: 7, value: notes !== null && notes !== void 0 ? notes : '' }) }), _jsx("div", { className: styles.editButton, children: _jsx(SecondaryActionButton, { id: "btn-edit-notes", onAction: () => setActiveModalSignal({
                                                type: 'metadata',
                                                value: {
                                                    author: projectAuthor,
                                                    notes,
                                                    start: projectStart,
                                                },
                                            }), title: txt('editVerb'), disabled: isReadOnly || isLocked }) })] })] })),
                    key: 'notesEdit',
                    className: styles.submenu,
                },
            ],
        },
        {
            key: 'tags',
            label: txt('tags'),
            icon: _jsx(TagsOutlined, {}),
            onClick: () => {
                if (collapsed) {
                    setActiveModalSignal({ type: 'tags' });
                }
            },
            children: !collapsed ? [{
                    label: (_jsx(TagsSection, { onProjectUpdate: enqueueProjectUpdate, isLocked: isLocked })),
                    className: clsx(styles.submenu, styles.tags),
                }] : null,
        },
        {
            key: 'advanced',
            label: txt('advancedSettings'),
            icon: _jsx(MoreOutlined, {}),
            children: [{
                    label: (_jsx(ProjectMenuAdvanced, { editorController: editorController, disabledChecks: disabledChecks, buttonClassName: collapsed ? styles.buttonDropdown : '' })),
                    key: 'advancedSettings',
                    className: `${styles.submenu} ${styles.advancedSettings}`,
                }],
        },
    ];
    const handleOpenChange = (openItems) => {
        if (!collapsed) {
            clientEnv.setMenuItemsOpen(openItems);
            setSubmenuItemsOpen(openItems);
        }
        if (openItems[0] === 'download') {
            beeyTutorialContext.tutorialGoNext();
        }
        if (openItems[0] === 'subtitleMode') {
            subModeTutorialContext.tutorialGoNext();
        }
    };
    const openKeysInTutorial = () => {
        if (beeyTutorialContext.currentStep === 6) {
            return (['download']);
        }
        return undefined;
    };
    return (_jsxs(Sider, { className: clsx(styles.projectMenuSider, { collapsed }), collapsed: collapsed, collapsible: true, collapsedWidth: "3.5rem", width: "250px", onCollapse: () => handleMenuCollapse(!collapsed), trigger: collapsed
            ? (_jsx(ResponsiveTooltip, { title: txt('expandSidebar'), open: tooltipVisible, onOpenChange: (visible) => setTooltipVisible(visible), children: _jsx(DoubleRightOutlined, { onMouseDown: () => setTooltipVisible(false) }) }))
            : (_jsx(ResponsiveTooltip, { title: txt('collapseSidebar'), open: tooltipVisible, onOpenChange: (visible) => setTooltipVisible(visible), children: _jsx(DoubleLeftOutlined, { onMouseDown: () => setTooltipVisible(false) }) })), children: [isMobile ? (_jsx(Drawer, { open: !collapsed, placement: "left", className: styles.drawer, onClose: () => handleMenuCollapse(true), children: _jsx(Menu, { items: items, mode: "inline", selectable: false, className: styles.projectMenuSider, openKeys: collapsed ? openKeysInTutorial() : submenuItemsOpen, onOpenChange: handleOpenChange }) })) : null, _jsx(Menu, { items: items, mode: "inline", selectable: false, className: styles.projectMenu, openKeys: collapsed ? openKeysInTutorial() : submenuItemsOpen, onOpenChange: handleOpenChange, triggerSubMenuAction: "click", theme: "light" }), _jsx(MemoizedProjectMenuModals, { isCaptionMode: isCaptionMode, editorController: editorController, isWaveVisible: isWaveVisible, onWaveVisibilityChange: onWaveVisibilityChange, enqueueProjectUpdate: enqueueProjectUpdate, exportTemplates: exportTemplates, isRightToLeft: isRightToLeft, projectFiles: projectFiles })] }));
};
export default React.memo(MemoizedProjectMenuPanel);
